import axios, { type AxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios';
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { Response, COMMAND_REDIRECT } from '../parsers/response';
import type { ServiceCallParams } from './servicecall-mixin';
import type { IronRequestElement } from '@polymer/iron-ajax/iron-request';

declare global {
  interface Window {
    TEMPLATE?: string;
    csrfToken?: string;
    PAYLOAD_JSON?: any;
    SERVICE_PAYLOAD?: any;
    REQUEST_PAYLOAD?: any;
    SECTION_SERVICE_PAYLOAD?: any;
  }
}

// Helper functions at module level
function getBaseUrl(): string {
  const location = window.location;
  let fullHost = `${location.protocol}//${location.hostname}`;
  
  if (location.port && location.port !== '80' && location.port !== '443') {
    fullHost += `:${location.port}`;
  }

  return fullHost;
}

// Get current CSRF token - function instead of static value
function getCsrfToken(): string {
  return window.csrfToken || '';
}

// Get headers dynamically - function instead of static object
function getRequestHeaders(): Record<string, string> {
  return {
    'X-CSRFToken': getCsrfToken(),
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  };
}

function mergeParams(params: ServiceCallParams, requestPayload: any): ServiceCallParams {
  const externalParams = typeof params === 'object' ? params : {};
  const payload = typeof requestPayload === 'object' ? requestPayload : {};
  return { ...externalParams, ...payload };
}

// Axios interceptor to always use the latest CSRF token
axios.interceptors.request.use(
  (config) => {
    // Always use the latest CSRF token from window
    if (config.headers) {
      config.headers['X-CSRFToken'] = getCsrfToken();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const httpMixin = <T extends Constructor<LitElement>>(BaseClass: T) => {
  class HttpMixin extends BaseClass {
    @property({ type: String })
    protocol = window.location.protocol;

    @property({ type: String })
    hostname = window.location.hostname;

    @property({ type: String })
    port = window.location.port;

    /**
     * Get base URL for API calls
     */
    protected baseUrl(): string {
      let fullHost = `${this.protocol}//${this.hostname}`;
      if (this.port && this.port !== '80' && this.port !== '443') {
        fullHost += `:${this.port}`;
      }

      return fullHost;
    }

    /**
     * Get current CSRF token dynamically
     */
    protected getCsrfToken(): string {
      return window.csrfToken || '';
    }

    /**
     * Get standard request headers dynamically
     */
    protected getRequestHeaders(): any {
      return {
        'X-CSRFToken': this.getCsrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      };
    }

    /**
     * Make an HTTP request with comprehensive error handling
     */
    async call(
      service: string, 
      params: ServiceCallParams = {}, 
      parseResponse = true
    ): Promise<Response | any> {
      
      // Handle offline mode
      if (window.NO_NETWORKING) {
        console.warn('Operating in offline mode with test data');
        const requestResponse = window.SECTION_SERVICE_PAYLOAD || {};
        const response = new Response(requestResponse);
        return response;
      }

      // Get fresh headers for each request
      const headers = this.getRequestHeaders();

      try {
        const response = await axios({
          url: `${this.baseUrl()}/GOTO/${service}/`,
          data: params,
          method: 'POST',
          headers,
          withCredentials: true,
        });

        const requestResponse = response.data;

        if (parseResponse) {
          return this.handleParsedResponse(requestResponse);
        } else {
          return requestResponse;
        }
      } catch (error) {
        this.handleRequestError(error);
        throw error;
      }
    }

    /**
     * Handle parsed response with success/error notifications
     */
    protected handleParsedResponse(requestResponse: any): Response {
      const response = new Response(requestResponse);

      // Handle redirects
      if (response.containsServiceCommand(COMMAND_REDIRECT)) {
        this.handleRedirect(response);
      }

      return response;
    }

    /**
     * Handle redirect commands
     */
    protected handleRedirect(response: Response): void {
      const redirect = response.parse(COMMAND_REDIRECT, false, false);
      const redirectTo = redirect.url;

      const link = document.createElement('a');
      link.href = redirectTo;
      
      if (link.pathname === window.location.pathname && link.search === window.location.search) {
        window.location.hash = link.hash;
        window.location.reload();
      } else {
        window.location.href = redirectTo;
      }
    }

    /**
     * Handle request errors
     */
    protected handleRequestError(error: any): void {
      console.error('HTTP request failed:', error);
    }

    /**
     * Send request using iron-request
     */
    protected async send(config: any): Promise<any> {
      // Ensure fresh CSRF token in config
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['X-CSRFToken'] = this.getCsrfToken();
      
      const ironRequest = document.createElement('iron-request') as IronRequestElement;
      return ironRequest.send(config);
    }
  }

  return HttpMixin as T & Constructor<HttpMixin>;
};

/**
 * Enhanced axios call function with better TypeScript support
 */
export const callAxios = async (
  service: string | undefined, 
  params: ServiceCallParams = {}, 
  parseResponse = true
): Promise<Response | any> => {
  // Merge with request payload
  if (window.REQUEST_PAYLOAD) {
    Object.keys(window.REQUEST_PAYLOAD).forEach((key) => {
      params[key] = window.REQUEST_PAYLOAD[key];
    });
  }

  const win = window as Window;

  if (win.NO_NETWORKING) {
    console.warn('Operating in offline mode with test data');
    const requestResponse = window.SECTION_SERVICE_PAYLOAD || {};
    return new Response(requestResponse);
  }

  const baseUrl = getBaseUrl();
  // Get fresh headers
  const headers = getRequestHeaders();
  const mergedParams = mergeParams(params, window.REQUEST_PAYLOAD || {});

  const config: AxiosRequestConfig = {
    method: 'POST',
    url: `${baseUrl}/GOTO/${service}/`,
    headers,
    data: mergedParams,
    withCredentials: true,
  };

  try {
    const response: AxiosResponse = await axios(config);
    const requestResponse = response.data;

    return parseResponse ? new Response(requestResponse) : requestResponse;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error('Axios request failed:', axiosError.message);
    throw error;
  }
};

// Export helper functions for external use
export { getCsrfToken, getRequestHeaders, getBaseUrl };

type Constructor<T = LitElement> = new (...args: any[]) => T;